/**
 * 星轨：沿时间螺线铺一串星尘，一颗彗星从第一段回忆流向最新的一段（着色器同原型的 TRAIL_VERT）。
 * 原型的螺线按回忆的先后排；正式版按日期排（一年一圈），所以星轨从第一张照片的日期走到最新一张，
 * 再往后是一段虚线，一直到今天之后一个月，那里写着「未完待续」（标签在 galaxy.ts）。
 * 点按弧长均匀铺开，颜色从第一段回忆的暖金过渡到最新的玫瑰色。
 * 路线（每天一个锚点、累计弧长）抽在 path.ts：漫游（roam.ts）的镜头沿同一条走。彗星平时按约 22 秒自己循环，
 * 漫游时由漫游推着走（uCometT >= 0，setComet）。
 */
import * as THREE from 'three';
import type { LocalDate } from '@star-sea/shared';
import { trailPath, type TrailPath } from './path.ts';

const VERT = /* glsl */ `
uniform float uTime;
uniform float uOpacity;
uniform float uEnd;
uniform float uDraw;
uniform float uComet;
uniform float uCometT;
uniform float uViewH;
uniform float uDpr;
attribute float aT;
attribute float aS;
attribute float aRnd;
varying float vA;
varying vec3 vC;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  float dist = max(-mv.z, 0.001);
  // 彗星：约 22 秒从第一段回忆走到最新一段；头是一团亮光，后面拖着渐暗的尾巴。
  // uCometT >= 0 时彗星头钉在那个位置（漫游推着走），小于 0 时照旧按时间自己循环。
  // 原型的螺线只有几十段回忆，这里一年一圈、点也更密，头和尾都收窄一些，免得在辉光里晕成一道弧
  float head = uCometT >= 0.0 ? uCometT : fract(uTime * 0.045);
  float d = aT - head;
  float pulse = uComet * (exp(-d * d * 4000.0) + 0.45 * exp(d * 60.0) * step(d, 0.0) * step(-0.12, d)) * step(aT, 1.0);
  // 最新一段之后是渐隐的虚线（按弧长每 1.2 个单位一段）
  float fade = 1.0 - smoothstep(1.0, uEnd, aT);
  float dotted = aT > 1.0 ? step(0.55, fract(aS / 1.2)) : 1.0;
  vA = (0.22 + 1.1 * pulse) * fade * dotted * step(aT, uDraw) * uOpacity * (0.6 + 0.4 * aRnd);
  vC = mix(vec3(1.0, 0.84, 0.62), vec3(1.0, 0.62, 0.78), clamp(aT, 0.0, 1.0));
  // 点径按世界大小算，太小的点按 1 像素画、亮度相应降低
  float px = (0.2 + 0.3 * pulse) * uViewH * 0.5 * projectionMatrix[1][1] / dist;
  vA *= clamp(px / uDpr, 0.2, 1.0);
  gl_PointSize = clamp(px, 1.0, 24.0 * uDpr);
  gl_Position = projectionMatrix * mv;
}
`;

const FRAG = /* glsl */ `
varying float vA;
varying vec3 vC;
void main() {
  float d = length(gl_PointCoord - 0.5);
  float a = smoothstep(0.5, 0.0, d);
  a = a * a * (1.0 + 2.0 * smoothstep(0.18, 0.0, d));
  gl_FragColor = vec4(vC, min(a, 1.0) * vA);
  #include <colorspace_fragment>
}
`;

/** 星轨上相邻两个点的间距（世界单位），点数限制在 600–7000 */
const SPACING = 0.12;

export class StarTrail {
  readonly points: THREE.Points<THREE.BufferGeometry, THREE.ShaderMaterial>;
  readonly uniforms = {
    uTime: { value: 0 },
    uOpacity: { value: 0 },
    uEnd: { value: 1.1 },
    uDraw: { value: 0 },
    uComet: { value: 1 },
    /** 彗星头在路线上的归一化位置（0–1 之间是实线段）；-1 是按约 22 秒自己循环，漫游时用 setComet 推着走 */
    uCometT: { value: -1 },
    uViewH: { value: 800 },
    uDpr: { value: 1 },
  };

  /** 最近一次 rebuild 铺的路线（漫游的镜头沿它走，保证和画出来的星轨是同一条）；clear 后是 null */
  private _path: TrailPath | null = null;
  get path(): TrailPath | null {
    return this._path;
  }

  constructor() {
    const material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.points = new THREE.Points(new THREE.BufferGeometry(), material);
    this.points.frustumCulled = false;
    this.points.renderOrder = 2;
    this.points.visible = false;
  }

  /**
   * 从 first（第一张照片的日期）铺到 end；last 是最新一张照片的日期，彗星只走 first → last 这一段。
   * 只有一天的照片时没有彗星，整条都是虚线。
   */
  rebuild(first: LocalDate, last: LocalDate, end: LocalDate, since: LocalDate): void {
    // 路线的锚点和弧长在 path.ts，漫游的镜头沿同一条走
    const path = (this._path = trailPath(first, last, end, since));
    const { days, anchors, arc, sLast, total } = path;
    const n = THREE.MathUtils.clamp(Math.round(total / SPACING), 600, 7000);
    const pos = new Float32Array(n * 3), tt = new Float32Array(n), ss = new Float32Array(n), rr = new Float32Array(n);
    const solid = sLast > 0.5;
    for (let i = 0, k = 0; i < n; i++) {
      // 按弧长均匀取点，在相邻两天的锚点之间插值
      const s = (i / (n - 1)) * total;
      while (k < days - 1 && arc[k + 1]! < s) k++;
      const f = THREE.MathUtils.clamp((s - arc[k]!) / Math.max(1e-6, arc[k + 1]! - arc[k]!), 0, 1);
      for (let c = 0; c < 3; c++) pos[i * 3 + c] = anchors[k * 3 + c]! + (anchors[k * 3 + 3 + c]! - anchors[k * 3 + c]!) * f;
      pos[i * 3] += (Math.random() - 0.5) * 0.5;
      pos[i * 3 + 1] += (Math.random() - 0.5) * 0.3;
      pos[i * 3 + 2] += (Math.random() - 0.5) * 0.5;
      tt[i] = solid ? s / sLast : 1 + s / Math.max(total, 1e-3);
      ss[i] = s;
      rr[i] = Math.random();
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('aT', new THREE.BufferAttribute(tt, 1));
    g.setAttribute('aS', new THREE.BufferAttribute(ss, 1));
    g.setAttribute('aRnd', new THREE.BufferAttribute(rr, 1));
    this.points.geometry.dispose();
    this.points.geometry = g;
    this.uniforms.uEnd.value = solid ? total / sLast : 2;
    this.points.visible = true;
  }

  /** 彗星头钉在归一化位置 t（漫游推着走，> 1 时在虚线尾巴上自然熄掉）；null 交还给按时间自己循环 */
  setComet(t: number | null): void {
    this.uniforms.uCometT.value = t ?? -1;
  }

  clear(): void {
    this._path = null;
    this.points.visible = false;
  }
}
